import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DataTable } from '@/components/shared/DataTable';
import { formatDate } from '@/utils/format';
import { toast } from 'sonner';

/**
 * Admin Users Management Component
 * Manage admin user accounts
 */
export const AdminUsersManagement = ({ API }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [adminForm, setAdminForm] = useState({
    name: '',
    email: '',
    password: ''
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAdmins(data);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(adminForm)
      });

      if (response.ok) {
        toast.success('Admin user created');
        await fetchAdmins();
        setShowDialog(false);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create admin user');
      }
    } catch (error) {
      console.error('Error creating admin user:', error);
      toast.error('Error creating admin user');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (admin) => {
    setEditingUser(admin);
    setAdminForm({ name: admin.name, email: admin.email, password: '' });
    setShowDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { name: adminForm.name, email: adminForm.email };
      if (adminForm.password) payload.password = adminForm.password;

      const response = await fetch(`${API}/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Admin user updated');
        await fetchAdmins();
        setShowDialog(false);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update admin user');
      }
    } catch (error) {
      toast.error('Error updating admin user');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this admin user?')) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/users/${adminId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.ok) {
        toast.success('Admin user deleted');
        await fetchAdmins();
      } else {
        toast.error('Failed to delete admin user');
      }
    } catch (error) {
      console.error('Error deleting admin user:', error);
      toast.error('Error deleting admin user');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setAdminForm({ name: '', email: '', password: '' });
    setEditingUser(null);
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (value) => (
        <div className="text-sm font-semibold text-gray-900">{value}</div>
      )
    },
    {
      key: 'email',
      label: 'Email',
      render: (value) => <div className="text-sm text-gray-900">{value}</div>
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value) => <div className="text-sm text-gray-600">{value ? formatDate(value) : 'N/A'}</div>
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-right',
      render: (_, admin) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(admin)}
            className="text-blue-600 hover:text-blue-700"
            data-testid={`edit-admin-${admin.id}`}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(admin.id)}
            className="text-red-600 hover:text-red-700"
            disabled={admin.email === 'admin@hotel.com'}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Admin Users</h2>
          <p className="text-sm text-gray-600 mt-1">
            {admins.length} admin user{admins.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          onClick={() => setShowDialog(true)}
          className="bg-gradient-to-r from-blue-600 to-indigo-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Admin User
        </Button>
      </div>

      {/* Admin Users Table */}
      <Card>
        <CardContent className="p-6">
          <DataTable
            columns={columns}
            data={admins}
            testIdPrefix="admin"
            emptyState={
              <div className="text-center text-gray-500 py-12">
                <p>No admin users found</p>
              </div>
            }
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">
              {editingUser ? 'Edit Admin User' : 'Add Admin User'}
            </h3>
            <form onSubmit={editingUser ? handleUpdate : handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={adminForm.name}
                  onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={adminForm.email}
                  onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editingUser ? 'New Password (leave blank to keep current)' : 'Password'}
                </label>
                <input
                  type="password"
                  value={adminForm.password}
                  onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                  required={!editingUser}
                  minLength={6}
                  placeholder={editingUser ? 'Leave blank to keep current' : ''}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowDialog(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : editingUser ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
